import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "../ui";
import { MEGA } from "../../data/nav";

const NAV = ["Features", "Use Cases", "Resources"];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const timer = useRef(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll); onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(null); setMobile(false); }, [loc.pathname, loc.search]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const enter = (item) => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(item), 120); };
  const leave = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(null), 120); };

  return (
    <header className={`sticky top-0 z-50 bg-surface ${scrolled ? "border-b border-line" : ""}`}>
      <div className="container-bc flex h-[72px] items-center justify-between" onMouseLeave={leave}>
        <div className="flex items-center gap-8">
          <Logo variant="lockup" />
          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
            {NAV.map((item) => (
              <div key={item} className="relative" onMouseEnter={() => enter(item)}>
                <button
                  className="flex items-center gap-1 text-sm text-ink-800 hover:text-ink-950 py-2"
                  aria-expanded={open === item} aria-haspopup="true"
                  onClick={() => setOpen(open === item ? null : item)}
                >
                  {item} <ChevronDown className="h-3.5 w-3.5 text-ink-400" />
                </button>
              </div>
            ))}
            <Link to="/pricing/" className="text-sm text-ink-800 hover:text-ink-950 py-2">Pricing</Link>
            <Link to="/reviews/" className="text-sm text-ink-800 hover:text-ink-950 py-2">Reviews</Link>
          </nav>
        </div>
        <div className="hidden lg:flex items-center gap-3">
          <Button to="/dashboard" variant="ghost" size="sm" data-testid="header-login-btn">Log in</Button>
          <Button href="https://tidycal.com/theryanslvdr/bconnect-intro" target="_blank" rel="noreferrer" variant="primary" size="sm" data-testid="header-talk-btn">Talk to Us</Button>
        </div>
        <button className="lg:hidden text-ink-800" aria-label="Open menu" onClick={() => setMobile(true)}><Menu className="h-6 w-6" /></button>

        {open && MEGA[open] && (
          <div className="absolute left-0 right-0 top-[72px] bg-surface border-b border-line" onMouseEnter={() => clearTimeout(timer.current)}>
            <div className="container-bc grid grid-cols-2 md:grid-cols-4 gap-8 py-8">
              {MEGA[open].columns.map((col) => (
                <div key={col.heading}>
                  <div className="eyebrow mb-4">{col.heading}</div>
                  <ul className="space-y-3">
                    {col.links.map((l) => (
                      <li key={l.href + l.label}>
                        <Link to={l.href} className="block group">
                          <span className="text-sm text-ink-900 group-hover:text-brand-700">{l.label}</span>
                          <span className="block text-xs text-ink-400">{l.desc}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <div className="card-hairline p-5 bg-surface-sunk">
                <div className="eyebrow mb-2">Featured</div>
                <Link to="/best/best-ai-model-for-sales-chatbots-2026/" className="text-sm text-ink-950 hover:text-brand-700">The 13-model benchmark</Link>
                <p className="text-xs text-ink-400 mt-1">We graded 13 AI models on reliability and cost per action. See who wins.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {mobile && (
        <div className="fixed inset-0 z-50 bg-surface lg:hidden flex flex-col">
          <div className="container-bc flex h-[72px] items-center justify-between border-b border-line">
            <Logo variant="lockup" />
            <button aria-label="Close menu" onClick={() => setMobile(false)}><X className="h-6 w-6 text-ink-800" /></button>
          </div>
          <div className="flex-1 overflow-y-auto container-bc py-6">
            {Object.keys(MEGA).map((item) => (
              <details key={item} className="border-b border-line py-3">
                <summary className="flex items-center justify-between text-base text-ink-950 list-none cursor-pointer">{item}<ChevronDown className="h-4 w-4" /></summary>
                <ul className="mt-3 space-y-2 pl-2">
                  {MEGA[item].columns.flatMap((c) => c.links).map((l) => (
                    <li key={l.href + l.label}><Link to={l.href} className="text-sm text-ink-600">{l.label}</Link></li>
                  ))}
                </ul>
              </details>
            ))}
            <Link to="/pricing/" className="block border-b border-line py-3 text-base text-ink-950">Pricing</Link>
            <Link to="/reviews/" className="block border-b border-line py-3 text-base text-ink-950">Reviews</Link>
          </div>
          <div className="container-bc py-4 border-t border-line flex flex-col gap-3">
            <Button to="/dashboard" variant="secondary" size="lg" className="w-full" data-testid="header-mobile-login-btn">Log in</Button>
            <Button href="https://tidycal.com/theryanslvdr/bconnect-intro" target="_blank" rel="noreferrer" variant="primary" size="lg" className="w-full" data-testid="header-mobile-talk-btn">Talk to Us</Button>
          </div>
        </div>
      )}
    </header>
  );
}
